<app-uni-page [parent]="this" *ngIf="isPageReady" pageTitle="{{ title }}" [bodyTemplate]="bodyTemplate"
  [footerTemplate]="footerTemplate">
</app-uni-page>

<ng-template #bodyTemplate>
  <p-toast></p-toast>

  <!-- Dashboard Header -->
  <div class="dashboard-header flex justify-between items-center p-3 border-b">
    <div class="flex gap-4 items-center">
      <span class="text-lg font-semibold">Widgets</span>
    </div>
    <div class="flex gap-2">
      <button
        pButton
        icon="pi pi-plus"
        label="Add Widget"
        class="p-button-outlined p-button-sm"
        (click)="onAddWidget()"
        pTooltip="Add a new widget to the dashboard">
      </button>
      <button
        pButton
        icon="pi pi-save"
        label="Save Layout"
        class="p-button-outlined p-button-sm"
        (click)="onSaveLayout()"
        pTooltip="Save your dashboard layout">
      </button>
      <button
        pButton
        icon="pi pi-refresh"
        label="Reset"
        class="p-button-outlined p-button-secondary p-button-sm"
        (click)="onResetLayout()"
        pTooltip="Reset to default layout">
      </button>
    </div>
  </div>

  <!-- Account Filter (for widgets that use account) -->
  <div class="flex gap-4 items-center p-3 border-b">
    <span class="text-sm text-gray-600">Account Filter:</span>
    <button pButton type="button" class="p-button-outlined p-button-sm" (click)="selectAccount()">
      <span>{{ account ? account.accountName : 'Select Account' }}</span>
    </button>
    <button *ngIf="account" pButton type="button" class="p-button-text p-button-sm" (click)="clearAccount()">
      <i class="pi pi-times"></i>
    </button>

    <label class="text-sm text-gray-600 ml-4" for="kpiRange">KPI Range:</label>
    <p-select inputId="kpiRange" [options]="kpiRangeOptions" optionLabel="label" optionValue="value"
      [(ngModel)]="kpiPreset" (onChange)="onKpiPresetChanged()" appendTo="body" size="small"></p-select>
    <ng-container *ngIf="kpiPreset === 'dateRange'">
      <p-datepicker inputId="kpiFromDate" [(ngModel)]="kpiFromDate" (ngModelChange)="onKpiRangeChanged()"
        placeholder="From" title="From date" appendTo="body" size="small" styleClass="dash-kpi-range-date"></p-datepicker>
      <p-datepicker inputId="kpiToDate" [(ngModel)]="kpiToDate" (ngModelChange)="onKpiRangeChanged()"
        placeholder="To" title="To date" appendTo="body" size="small" styleClass="dash-kpi-range-date"></p-datepicker>
    </ng-container>

    <button pButton type="button" icon="pi pi-eye-slash" class="p-button-outlined p-button-sm ml-4" (click)="showExcludedAccounts()">
      <span>Show Excluded Accounts</span>
    </button>
    <span *ngIf="includedAccountIds.length > 0" class="text-sm" style="color: var(--p-primary-color)">
      This view includes {{ includedAccountIds.length }} excluded {{ includedAccountIds.length === 1 ? 'account' : 'accounts' }}.
    </span>
  </div>

  <!-- Gridster Dashboard -->
  <div class="dashboard-grid-container">
    <gridster [options]="gridsterOptions">
      <gridster-item *ngFor="let widget of dashboardConfig.widgets; trackBy: trackByWidgetId" [item]="widget" class="dashboard-widget"
        [class.dash-kpi-tint]="isKpi(widget.widgetType)">
        <!-- Widget Header -->
        <div class="widget-header flex justify-between items-center p-2 cursor-move border-b">
          <span class="font-medium text-sm flex items-center gap-2">
            <span *ngIf="isKpi(widget.widgetType)" class="dash-kpi-badge">KPI</span>
            <span *ngIf="usesAccountFilter(widget.widgetType)" class="dash-filter-badge" title="This widget uses the Account Filter.">FILTER</span>
            <span>{{ getWidgetTitle(widget.widgetType) }}</span>
          </span>
          <div class="flex gap-1">
            <button
              *ngIf="supportsDataView(widget.widgetType)"
              pButton
              icon="pi pi-table"
              class="p-button-text p-button-sm p-button-rounded"
              (click)="onViewWidgetData(widget)"
              pTooltip="View data in grid">
            </button>
            <button
              *ngIf="supportsDataView(widget.widgetType)"
              pButton
              icon="pi pi-download"
              class="p-button-text p-button-sm p-button-rounded"
              (click)="onExportWidget(widget)"
              pTooltip="Export to Excel">
            </button>
            <button
              pButton
              icon="pi pi-times"
              class="p-button-text p-button-danger p-button-sm p-button-rounded"
              (click)="onRemoveWidget(widget)"
              pTooltip="Remove widget">
            </button>
          </div>
        </div>

        <!-- Widget Content -->
        <div class="widget-content">
          <ng-container [ngSwitch]="widget.widgetType">
            <app-jo-in-process-widget *ngSwitchCase="'jo-in-process'" [account]="account" [includedAccountIds]="includedAccountIds"></app-jo-in-process-widget>
            <app-chart-jo-stats *ngSwitchCase="'jo-stats'" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-chart-jo-stats>
            <app-chart-invoice-stats *ngSwitchCase="'invoice-stats'" [account]="account" class="widget-chart"></app-chart-invoice-stats>
            <app-chart-invoice-delay *ngSwitchCase="'invoice-delay'" [account]="account" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-chart-invoice-delay>
            <app-chart-manuf-inventory *ngSwitchCase="'inventory-count'" class="widget-chart"></app-chart-manuf-inventory>
            <app-chart-product-type-inventory *ngSwitchCase="'product-type-inventory'" class="widget-chart"></app-chart-product-type-inventory>
            <app-chart-account-fulfillment-time *ngSwitchCase="'account-fulfillment-time'" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-chart-account-fulfillment-time>
            <app-chart-manufacturer-receipt-time *ngSwitchCase="'manufacturer-receipt-time'" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-chart-manufacturer-receipt-time>
            <app-account-overdue-orders *ngSwitchCase="'account-overdue-orders'" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-account-overdue-orders>
            <app-chart-weekly-shipment-revenue *ngSwitchCase="'weekly-shipment-revenue'" class="widget-chart"></app-chart-weekly-shipment-revenue>
            <app-red-zone *ngSwitchCase="'red-zone'" [account]="account" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-red-zone>
            <app-on-time-shipped-kpi *ngSwitchCase="'on-time-shipped-count'" measure="count" [kpiDates]="kpiDates" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-on-time-shipped-kpi>
            <app-on-time-shipped-kpi *ngSwitchCase="'on-time-shipped-value'" measure="value" [kpiDates]="kpiDates" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-on-time-shipped-kpi>
            <app-orders-shipped-complete-kpi *ngSwitchCase="'orders-shipped-complete'" [kpiDates]="kpiDates" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-orders-shipped-complete-kpi>
            <app-process-station-units-kpi *ngSwitchCase="'process-station-units'" [kpiDates]="kpiDates" [includedAccountIds]="includedAccountIds" class="widget-chart"></app-process-station-units-kpi>
            <div *ngSwitchDefault class="p-4 text-gray-500">Unknown widget type</div>
          </ng-container>
        </div>
      </gridster-item>
    </gridster>
  </div>
</ng-template>

<ng-template #footerTemplate>
  <div class="left-buttons">
    <button pButton type="button" class="p-button-raised" icon="pi pi-arrow-left" (click)="goBack()">
      <span>Go Back</span>
    </button>
  </div>
</ng-template>
